/**
 * PostHog Analytics Utilities
 *
 * Provides wrapper functions for PostHog analytics and error tracking.
 * These functions safely handle cases where PostHog is not initialized.
 */

import posthog from 'posthog-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { isUnexpectedApiFailure } from '@/common/apiRequestError';

// PostHog configuration
const POSTHOG_HOST_DIRECT = 'https://us.i.posthog.com';
const POSTHOG_HOST_PROXY = '/phs';
export const POSTHOG_API_KEY = 'phc_lU9R7OyaXldpBlRUpPg8ZP78nLbcQy1ucdAo7m3bXHs';

// Domains where PostHog should run
const ALLOWED_ROOTS = ['recruit41.com', 'learn41.com', 'devr41.com', 'devl41.com'];

const hostname = window.location.hostname;

// Check if hostname matches primary or subdomain (*.root.com)
const isAllowedDomain = ALLOWED_ROOTS.some(
  (root) => hostname === root || hostname.endsWith(`.${root}`),
);

// Only enable PostHog when in allowed domain
export const isPostHogEnabled = isAllowedDomain && Boolean(POSTHOG_API_KEY);

/**
 * Determine PostHog API host based on current domain.
 * Uses reverse proxy in deployed environments to bypass ad blockers.
 * Same pattern as Monaco Editor CDN proxy.
 *
 * Architecture:
 * - devr41.com, qar41.com, recruit41.com: /phs/* -> GCP Load Balancer -> us.i.posthog.com
 * - localr41.com: Direct to us.i.posthog.com (no local proxy)
 *
 * Note: Using '/phs' instead of '/ingest' because '/ingest' is a known PostHog
 * proxy path that's included in ad blocker filter lists.
 */
function getPostHogHost(): string {
  const parts = hostname.split('.');
  const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname;

  // Local development doesn't have the proxy in nginx
  if (baseDomain === 'localr41.com') {
    return POSTHOG_HOST_DIRECT;
  }

  // Deployed environments use the reverse proxy (bypasses ad blockers)
  return POSTHOG_HOST_PROXY;
}

const POSTHOG_HOST = getPostHogHost();

// Log which PostHog host is being used (helpful for debugging)
if (isPostHogEnabled && (hostname.includes('localr41.com') || hostname.includes('devr41.com'))) {
  console.log(`[PostHog] Loading from: ${POSTHOG_HOST}`);
}

// PostHog options - session recording disabled by default
export const posthogOptions = {
  api_host: POSTHOG_HOST,
  defaults: '2025-11-30',
  person_profiles: 'identified_only' as const, // Anonymous by default, identified after identify() call
  enable_heatmaps: false, // Enabled per-route via posthog.set_config() in candidate profile
  disable_session_recording: true, // Disabled by default, enabled only on meeting routes
  session_recording: {
    recordCrossOriginIframes: true,
    console_log_recording_enabled: true, // Capture full console logs
  },
} as const;

/**
 * Identify a user in PostHog with their properties
 * @param userId - Unique user identifier
 * @param userProperties - Additional user properties to track
 */
function identifyUser(
  userId: string | number,
  userProperties?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.identify(String(userId), userProperties);
    }
  } catch (error) {
    console.error('Failed to identify user in PostHog:', error);
  }
}

/**
 * Reset the current user (e.g., on logout)
 * This clears the user identity and generates a new anonymous ID
 */
function resetUser(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.reset();
    }
  } catch (error) {
    console.error('Failed to reset user in PostHog:', error);
  }
}

function describeThrown(thrown: unknown): string {
  try {
    const json = JSON.stringify(thrown);
    if (json && json !== '{}') return json.slice(0, 200);
  } catch {
    // Circular or otherwise unserialisable, fall through to String().
  }
  return String(thrown);
}

/**
 * PostHog records a thrown non-Error as "Non-Error promise rejection captured
 * with value: ...", which carries no stack and groups every unrelated throw site
 * into a single issue. Normalising here catches the throwables no call site owns:
 * rejected strings, DOM events, API response objects.
 */
function toError(thrown: unknown): Error {
  if (thrown instanceof Error) return thrown;

  if (typeof thrown === 'string') return new Error(thrown);

  if (thrown && typeof thrown === 'object') {
    const shape = thrown as { message?: unknown; detail?: unknown };
    const message =
      typeof shape.message === 'string'
        ? shape.message
        : typeof shape.detail === 'string'
          ? shape.detail
          : `Non-Error thrown: ${describeThrown(thrown)}`;
    return new Error(message, { cause: thrown });
  }

  return new Error(`Non-Error thrown: ${String(thrown)}`, { cause: thrown });
}

function readHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const shape = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof shape.status === 'number') return shape.status;
  if (typeof shape.response?.status === 'number') return shape.response.status;
  return undefined;
}

function readResponseData(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  const shape = error as { data?: unknown; response?: { data?: unknown } };
  return shape.data ?? shape.response?.data;
}

function newFailureId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().slice(0, 8);
    }
  } catch {
    // Non-secure context, fall through.
  }
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Capture an exception in PostHog for error tracking
 * @param error - The error object or message to capture
 * @param context - Additional context about the error
 */
export function captureException(
  error: Error | string | unknown,
  context?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      const normalized = toError(error);

      posthog.captureException(normalized, {
        ...context,
        errorMessage: normalized.message,
        errorStack: normalized.stack,
      });
    }
  } catch (captureError) {
    console.error('Failed to capture exception in PostHog:', captureError);
  }
}

/**
 * posthog-js merges event properties over registered super properties with
 * `Object.assign`, which copies a key whose value is `undefined`. An id that has
 * not resolved yet would therefore erase the registered one rather than fall back
 * to it, so a half-loaded page reports worse attribution than no property at all.
 */
export function definedOnly(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!properties) return properties;
  const defined = Object.entries(properties).filter(([, value]) => value !== undefined);
  return defined.length === Object.keys(properties).length
    ? properties
    : Object.fromEntries(defined);
}

/**
 * Report a failure that both matters to a funnel and threw.
 *
 * Emits two records sharing a `failure_id`: the analytics event keeps the step
 * countable, the exception keeps the stack, and the id lets you pivot between
 * them. Reporting only the event is what left every catch block in the client
 * without a stack trace.
 *
 * Use `captureEvent` alone when nothing threw: an expected 404, a validation
 * rejection, a user cancelling, or a UX state such as a logo failing to load.
 */
export function captureFailure(
  eventName: string,
  error: unknown,
  properties?: Record<string, any>,
): void {
  const failureId = newFailureId();
  const normalized = toError(error);
  const status = readHttpStatus(error);
  const statusProperty = status === undefined ? {} : { error_status: status };

  // Same rule `fetchWithCsrf` applies, so the two halves agree. Without it an
  // expected 403 reported through here files exactly the crash that
  // `fetchWithCsrf` just declined to file, and the 4xx noise returns by the
  // other door. Anything that did not carry a status is reported.
  if (status === undefined || isUnexpectedApiFailure(status, readResponseData(error))) {
    captureException(normalized, {
      ...properties,
      ...statusProperty,
      failure_id: failureId,
      failure_event: eventName,
    });
  }

  // A typed wrapper keeps the browser's own error in `cause`, so without this every
  // transport failure reports one constant string and the real reason is never seen.
  const cause = normalized.cause;
  const causeProperty =
    cause instanceof Error ? { error_cause: `${cause.name}: ${cause.message}`.slice(0, 200) } : {};

  // Caller properties are spread last so an existing domain value such as
  // `error_type: 'api_error'` keeps its meaning and the insights built on it
  // keep working. Only `failure_id` is always ours.
  captureEvent(eventName, {
    error_type: normalized.name,
    error_message: normalized.message.slice(0, 200),
    ...statusProperty,
    ...causeProperty,
    ...properties,
    failure_id: failureId,
  });
}

/**
 * Capture a custom event in PostHog
 * @param eventName - Name of the event
 * @param properties - Event properties
 */
export function captureEvent(
  eventName: string,
  properties?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.capture(eventName, definedOnly(properties));
    }
  } catch (error) {
    console.error('Failed to capture event in PostHog:', error);
  }
}

/**
 * Capture an event using sendBeacon transport for reliability during page unload.
 * Use this instead of captureEvent inside useEffect cleanup / beforeunload handlers
 * where the normal XHR transport may be cancelled by the browser.
 */
export function captureBeaconEvent(
  eventName: string,
  properties?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.capture(eventName, definedOnly(properties), { transport: 'sendBeacon' });
    }
  } catch (error) {
    console.error('Failed to capture beacon event in PostHog:', error);
  }
}

/**
 * Set user properties that will be sent with all future events
 * @param properties - Properties to set for the user
 */
function setUserProperties(properties: Record<string, any>): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.people.set(properties);
    }
  } catch (error) {
    console.error('Failed to set user properties in PostHog:', error);
  }
}

/**
 * Start session recording in PostHog
 * Used when user enters meeting view routes
 */
export function startSessionRecording(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.startSessionRecording();
    }
  } catch (error) {
    console.error('Failed to start session recording in PostHog:', error);
  }
}

/**
 * Stop session recording in PostHog
 * Used when user leaves meeting view routes
 */
export function stopSessionRecording(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.stopSessionRecording();
    }
  } catch (error) {
    console.error('Failed to stop session recording in PostHog:', error);
  }
}

/**
 * Set tenant context as a super property in PostHog
 * This property will be sent with all subsequent events
 * @param tenantName - The tenant name to track
 */
export function setTenantContext(tenantName: string): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.register({
        tenant_name: tenantName,
      });
    }
  } catch (error) {
    console.error('Failed to set tenant context in PostHog:', error);
  }
}

/**
 * Set interview context as super properties in PostHog
 * These properties will be sent with all subsequent events
 * Allows filtering events by interview_id in PostHog dashboard
 * @param interviewId - The interview ID to track
 */
export function setInterviewContext(interviewId: string): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.register({
        interview_id: interviewId,
      });
    }
  } catch (error) {
    console.error('Failed to set interview context in PostHog:', error);
  }
}

/**
 * React hook to read a PostHog boolean feature flag.
 * Returns false while PostHog is still loading the flag definition,
 * so callers default to "flag off" during the initial render window.
 */
export function useFeatureFlag(flagName: string): boolean {
  const enabled = useFeatureFlagEnabled(flagName);
  return enabled === true;
}

/**
 * Force a fresh flag fetch from PostHog, then return the boolean value.
 * Use at decision points (e.g., Join button) where the hook's ~30s cache
 * would let a user slip through a kill-switch that was just flipped.
 * Falls back to the cached value if PostHog is unavailable or the reload
 * doesn't resolve within timeoutMs.
 */
export function reloadAndCheckFlag(
  flagName: string,
  timeoutMs = 2000,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (!posthog || !posthog.__loaded) {
      resolve(false);
      return;
    }

    let settled = false;
    let unsubscribe: (() => void) | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
      resolve(value);
    };

    try {
      unsubscribe = posthog.onFeatureFlags(() => {
        finish(posthog.isFeatureEnabled(flagName) === true);
      });
      posthog.reloadFeatureFlags();
    } catch {
      finish(posthog.isFeatureEnabled(flagName) === true);
      return;
    }

    timeoutId = setTimeout(
      () => finish(posthog.isFeatureEnabled(flagName) === true),
      timeoutMs,
    );
  });
}

/**
 * Clear interview context from PostHog super properties
 * Used when user leaves interview routes
 */
export function clearInterviewContext(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.unregister('interview_id');
    }
  } catch (error) {
    console.error('Failed to clear interview context in PostHog:', error);
  }
}
